import { push } from "connected-react-router";
import { channel } from "redux-saga";
import { cancelled } from "redux-saga-test-plan/matchers";
import { fork, put, select, take, takeEvery, takeLatest } from "redux-saga/effects";
import invariant from "tiny-invariant";

import { EntityType } from "@mapmycustomers/shared/enum";
import { Company } from "@mapmycustomers/shared/types/entity";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import Path from "@app/enum/Path";
import RouteSubPath from "@app/enum/RouteSubPath";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam";

import { openEntity, showEntityView, showRandomRecord } from "./actions";

type EntityViewChannelAction = ReturnType<typeof openEntity> | ReturnType<typeof showEntityView>;

export let showEntityChannel = channel<EntityViewChannelAction>();

export function* onPreview(): Generator {
  showEntityChannel = channel<EntityViewChannelAction>();
  try {
    while (true) {
      const action: EntityViewChannelAction = yield take(showEntityChannel);
      yield put(action);
    }
  } finally {
    if (yield cancelled()) {
      showEntityChannel.close();
    }
  }
}

export function* onOpenEntity({
  payload: { entityId, entityType },
}: ReturnType<typeof openEntity>) {
  switch (entityType) {
    case EntityType.ROUTE:
      yield put(push(`${Path.ROUTE}/${RouteSubPath.VIEW}/${entityId}`));
      break;
    default:
      invariant(false, `The "${entityType}" type is not supported by openEntity yet.`);
  }
}

export function* onShowEntityView({ payload: { tab } }: ReturnType<typeof showEntityView>) {
  if (tab) {
    yield put(push(`#${tab}`));
  }
}

export function* onShowRandomRecord() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const response: ListResponse<Company> = yield callApi("fetchCompanies", orgId, {
      $limit: 1,
      $order: "-modifiedAt",
    });

    if (response.data.length) {
      const company = response.data[0];
      yield put(showEntityView({ entityId: company.id, entityType: company.entity }));
    }
  } catch (error) {
    handleError({ error });
  }
}

export function* entityViewSaga() {
  yield fork(onPreview);
  yield takeEvery(openEntity, onOpenEntity);
  yield takeEvery(showEntityView, onShowEntityView);
  yield takeLatest(showRandomRecord, onShowRandomRecord);
}
